"use client";

import { useState, useSyncExternalStore, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

// Stable subscribe that never fires — `useSyncExternalStore` is the
// canonical way to read client-only browser APIs in React 19 without
// triggering the impure-call lint or hydration mismatch warnings.
function noopSubscribe() {
  return () => {};
}
function isAuthedNow(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(loadTokens());
}
function getNow(): number {
  if (typeof window === "undefined") return 0;
  return Date.now();
}

interface Props {
  marketSlug: string;
  resolvedAt: string;
  /** dispute_window_hours — server uses 24h default. */
  windowHours?: number;
}

/**
 * STORY-309d phase 1 — user-facing "Dispute this resolution" entry
 * point. Visible only inside the dispute window (24h after
 * resolved_at) and only for logged-in users. Posts to
 * /v1/markets/<slug>/disputes with a 30+ char reason and optional
 * evidenceUrl.
 *
 * Renders as a subtle inline link by default; clicking expands an
 * inline form. Keeps the resolved banner unobtrusive — disputes are
 * the exception path, not the default.
 */
export function DisputeResolutionLink({
  marketSlug,
  resolvedAt,
  windowHours = 24,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Client-only auth + window check via useSyncExternalStore. The
  // server snapshot returns false so SSR doesn't render the link;
  // hydration replaces with the real client snapshot.
  const isAuthed = useSyncExternalStore(
    noopSubscribe,
    isAuthedNow,
    () => false,
  );
  const now = useSyncExternalStore(noopSubscribe, getNow, () => 0);
  const expiresAt =
    new Date(resolvedAt).getTime() + windowHours * 60 * 60 * 1000;
  if (now === 0) return null; // SSR
  if (now > expiresAt) return null;
  if (!isAuthed) return null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (reason.trim().length < 30) {
      setError(t("disputeForm.errReasonTooShort"));
      return;
    }
    setSubmitting(true);
    try {
      await api.fileDispute(marketSlug, {
        reason: reason.trim(),
        evidenceUrl: evidenceUrl.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t("disputeForm.errNetwork"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        role="status"
        className="mt-2 rounded-md border px-3 py-2 text-[12px]"
        style={{
          borderColor:
            "color-mix(in srgb, var(--color-status-positive) 50%, transparent)",
          background:
            "color-mix(in srgb, var(--color-status-positive) 8%, transparent)",
          color: "var(--color-text)",
        }}
      >
        {t("disputeForm.successBody")}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-block text-[11px] text-[var(--color-text3)] underline transition-colors hover:text-[var(--color-text2)]"
      >
        {t("disputeForm.openLink")}
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3"
    >
      <p className="text-[11px] text-[var(--color-text3)]">
        {t("disputeForm.helperText")}
      </p>
      <label className="flex flex-col gap-1 text-[11px]">
        <span className="font-semibold text-[var(--color-text2)]">
          {t("disputeForm.reasonLabel")}
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          minLength={30}
          maxLength={2000}
          required
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 text-[12px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-brand)]"
        />
        <span className="text-[10px] text-[var(--color-text3)]">
          {reason.trim().length}/30 min · {reason.trim().length}/2000 max
        </span>
      </label>
      <label className="flex flex-col gap-1 text-[11px]">
        <span className="font-semibold text-[var(--color-text2)]">
          {t("disputeForm.evidenceLabel")}
        </span>
        <input
          type="url"
          value={evidenceUrl}
          onChange={(e) => setEvidenceUrl(e.target.value)}
          placeholder="https://"
          maxLength={500}
          className="h-8 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-brand)]"
        />
      </label>
      {error && (
        <div
          role="alert"
          className="rounded-md border px-2 py-1 text-[11px]"
          style={{
            borderColor:
              "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
            background:
              "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
            color: "var(--color-status-negative)",
          }}
        >
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60"
          style={{ background: "var(--color-brand)" }}
        >
          {submitting
            ? t("disputeForm.submitting")
            : t("disputeForm.submit")}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11px] text-[var(--color-text3)] underline hover:text-[var(--color-text2)]"
        >
          {t("disputeForm.cancel")}
        </button>
      </div>
    </form>
  );
}
